import React from 'react';
import './AdminDashCoachingMeetings.styl';
import {List, Map} from 'immutable';
import {compareNumeric} from 'components/coaching/landing-pages/helpers/tableSorts';
import {ColorDot} from 'components/utils/ColorDot';
import {sumBy} from 'utils/sum';
import {MeetingMetricsCard} from 'components/dashboard/admin/admin-coaching-meetings/MeetingMetricsCard';
import {Sneezeguard} from 'components/dashboard/admin/admin-coaching-meetings/components/Sneezeguard';
import './AdminDashCoachingMeetings.styl';
import MultiselectDropdown, {
  SelectedStyle,
  MultiselectDropdownOption,
} from 'charts/components/forms/MultiselectDropdown';
import SimpleDropdown from 'charts/components/forms/SimpleDropdown';
import User from 'records/User';
import CoachingMeetingCategory from 'records/CoachingMeetingCategory';
import {DashChild} from '@store/@types/dash/DashChild';
import {browserHistory} from 'react-router';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';

// the default sort - extract so we can pluck it out later
const coachingTimeIdentifier = 'Coaching Time';

interface AdminDashCoachingMeetingsProps extends DashChild {
  readonly meetingCategories?: Map<string, CoachingMeetingCategory>;
  readonly meetingCategoryTimeInSeconds?: List<any>;
  readonly meetingCategoryCount?: List<any>;
  readonly meetingCount?: Map<string, number>;
  readonly planCount?: Map<string, number>;
  readonly improvement?: Map<string, number>;
  readonly teamIds?: List<string>;
  readonly users?: List<User>;
  readonly adminId?: string;
  readonly coaches?: List<User>;
  readonly loaded: boolean;
  readonly fromPath: any; // todo any
}

interface AdminDashCoachingMeetingsState {
  selectedCoaches: Map<string, MultiselectDropdownOption>;
  selectedSortId?: string;
  displayingPopoverFor?: string;
}

export default class AdminDashCoachingMeetings extends React.Component<
  AdminDashCoachingMeetingsProps,
  AdminDashCoachingMeetingsState
> {
  constructor(props) {
    super(props);

    this.onAddCoachFilter = this.onAddCoachFilter.bind(this);
    this.onRemoveCoachFilter = this.onRemoveCoachFilter.bind(this);
    this.setSortType = this.setSortType.bind(this);

    this.state = {
      selectedCoaches: Map(),
      selectedSortId: undefined,
      displayingPopoverFor: undefined,
    };
  }

  coachNameClick = coach => {
    const fromPath = this.props.fromPath;
    const userInUserStore = this.props.coaches?.find(u => u.userId === coach.id);
    const roleToUse = userInUserStore?.isTeamManager ? 'manager' : 'coach';

    const state = fromPath
      ? {
          pathname: fromPath,
          action: HISTORY_ACTIONS.PUSH,
        }
      : {};
    const link = {
      pathname: `/dashv2/admin/${this.props.adminId}/${roleToUse}/${coach.userId}`,
      state,
    };
    browserHistory.push(link);
  };

  /**
   * Max for the rollup values.
   *
   * @param meetingCategoryTimeInSeconds
   * @return {*|number}
   */
  getMaxMeetingCategoryTime(meetingCategoryTimeInSeconds) {
    const maxItem = meetingCategoryTimeInSeconds.maxBy(i => i.value);

    return (maxItem && maxItem.value) || 0;
  }

  /**
   * given coaches, calc their total meeting time in seconds
   *
   * @param coaches
   * @param meetingCategoryTimeInSeconds
   *
   * @return map of coachId -> time in seconds
   */
  getMeetingTimePerCoach(coaches, meetingCategoryTimeInSeconds): Map<string, number> {
    return Map(
      coaches.map(c => {
        const perCoach = meetingCategoryTimeInSeconds.filter(it => it.id === c.id);

        const summed = sumBy(perCoach, i => i.value);

        return [c.id, summed || 0];
      })
    );
  }

  setSortType(sortType) {
    this.setState({selectedSortId: sortType.id});
  }

  onAddCoachFilter(coachFilter) {
    this.setState({
      selectedCoaches: this.state.selectedCoaches.set(coachFilter.id, coachFilter),
    });
  }

  onRemoveCoachFilter(coachFilter) {
    this.setState({
      selectedCoaches: this.state.selectedCoaches.delete(coachFilter.id),
    });
  }

  getSorts(
    meetingCategories: Map<string, CoachingMeetingCategory>,
    meetingTimePerCoach: Map<string, number>
  ) {
    const {meetingCategoryTimeInSeconds, meetingCount, planCount} = this.props;

    const meetingCategorySorts = meetingCategories.map(mc => {
      return {
        id: mc.name,
        name: (
          <div className="cat-selector">
            <ColorDot size={10} color={mc.color} />
            <span>{mc.name}</span>
          </div>
        ),
        sort: (a, b) => {
          const aT = sumBy(
            meetingCategoryTimeInSeconds?.filter(
              mct => mct.id === a.id && parseInt(mct.context, 10) === mc.id
            ),
            i => i.value
          );

          const bT = sumBy(
            meetingCategoryTimeInSeconds?.filter(
              mct => mct.id === b.id && parseInt(mct.context, 10) === mc.id
            ),
            i => i.value
          );

          return compareNumeric(bT, aT);
        },
      };
    });

    return [
      ...[
        {
          id: 'Alphabetical',
          name: (
            <div className="non-meeting-type-sort">
              <span>Alphabetical</span>
            </div>
          ),
          sort: (a, b) => a.getFullName().localeCompare(b.getFullName()),
        },
        {
          id: coachingTimeIdentifier,
          name: (
            <div className="non-meeting-type-sort">
              <span>{coachingTimeIdentifier}</span>
            </div>
          ),
          sort: (a, b) => {
            const aT = meetingTimePerCoach.get(a.id);
            const bT = meetingTimePerCoach.get(b.id);

            return compareNumeric(bT, aT);
          },
        },
        {
          id: 'Meeting Count',
          name: (
            <div className="non-meeting-type-sort">
              <span>Meeting Count</span>
            </div>
          ),
          sort: (a, b) => {
            const aT = meetingCount?.get(a.id);
            const bT = meetingCount?.get(b.id);

            return compareNumeric(bT, aT);
          },
        },
        {
          id: 'Plan Count',
          name: (
            <div className="non-meeting-type-sort">
              <span>Plan Count</span>
            </div>
          ),
          sort: (a, b) => {
            const aT = planCount?.get(a.id);
            const bT = planCount?.get(b.id);

            return compareNumeric(bT, aT);
          },
        },
      ],
      ...meetingCategorySorts.toList().toArray(),
    ];
  }

  render() {
    const {
      coaches = List(),
      meetingCategories: allMeetingCategories,
      meetingCategoryTimeInSeconds,
      meetingCategoryCount,
      meetingCount,
      planCount,
      improvement,
      teamIds,
      startDate,
      endDate,
      fromPath,
    } = this.props;
    if (!allMeetingCategories) return null;

    const {selectedCoaches, selectedSortId, displayingPopoverFor} = this.state;

    const maxMeetingCategoryTime = this.getMaxMeetingCategoryTime(meetingCategoryTimeInSeconds);

    const meetingTimePerCoach = this.getMeetingTimePerCoach(coaches, meetingCategoryTimeInSeconds);

    // we hide any that the account has specified should be hidden. See external admin for more
    const meetingCategories = allMeetingCategories.filter(c => !c.hidden);

    // TODO add individual metric sorts to this
    const allSorts = this.getSorts(meetingCategories, meetingTimePerCoach);

    const activeSort = allSorts.find(
      it => it.id === (selectedSortId ? selectedSortId : coachingTimeIdentifier)
    );

    const timeCard = (c, i) => (
      <MeetingMetricsCard
        key={c.id}
        coach={c}
        meetingCategories={meetingCategories}
        maxMeetingCategoryTime={maxMeetingCategoryTime}
        meetingCategoryTimeInSeconds={meetingCategoryTimeInSeconds?.filter(mct => mct.id === c.id)}
        meetingTime={meetingTimePerCoach.get(c.id)}
        meetingCategoryCount={meetingCategoryCount?.filter(mcc => mcc.id === c.id)}
        planCount={planCount?.get(c.id)}
        meetingCount={meetingCount?.get(c.id)}
        improvement={improvement?.get(c.id)}
        teamIds={teamIds}
        startDate={startDate}
        endDate={endDate}
        fromPath={fromPath}
        left={i % 2 === 0}
        onPopoverOpen={coachId => this.setState({displayingPopoverFor: coachId})}
        onPopoverClose={() => this.setState({displayingPopoverFor: undefined})}
        popped={c.id === displayingPopoverFor}
        coachNameClick={this.coachNameClick}
      />
    );

    return (
      <div className="admin-dash-coaching-meetings standalone-widget-wrapper">
        {displayingPopoverFor && <Sneezeguard />}
        <div className="filter-sorting-widgets">
          <div className="filter">
            <label>Search</label>
            <MultiselectDropdown
              selectedValues={selectedCoaches.toList().toJS()}
              syncSelections={coaches
                .map(c => ({
                  id: c.id,
                  name: c.getFullName(),
                }))
                .toJS()}
              asyncSelectionsSource={undefined}
              selectedStyle={SelectedStyle.PILL_STYLE}
              onSelect={this.onAddCoachFilter}
              onRemoveSelection={this.onRemoveCoachFilter}
            />
          </div>
          <div className="sorting">
            <label>Sort by </label>
            <SimpleDropdown
              selections={allSorts}
              onSelect={this.setSortType}
              value={activeSort}
              width={250}
            />
          </div>
        </div>
        {selectedCoaches.size > 0 && (
          <div className="meeting-display">
            <div className="hr">
              <label>Selected Coaches</label>
              <div className="rule" />
            </div>
            {coaches
              ?.filter(c => selectedCoaches.has(c.id))
              .sort((a, b) => activeSort?.sort(a, b))
              .map(timeCard)}
          </div>
        )}
        <div className="meeting-display">
          {selectedCoaches.size > 0 && (
            <div className="hr">
              <label>Other Coaches</label>
              <div className="rule" />
            </div>
          )}
          {coaches
            ?.filter(c => !selectedCoaches.has(c.id))
            .sort((a, b) => activeSort?.sort(a, b))
            .map(timeCard)}
        </div>
      </div>
    );
  }
}
